import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
// 【変更点】Paginationを追加
import { Container, Card, Spinner, Alert, Button, Accordion, Row, Col, Table, Form, Badge, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Clipboard } from 'react-bootstrap-icons';

// 【新規追加】発注番号をハイフン区切りにフォーマットするヘルパー関数
const formatDisplayOrderNumber = (orderNumber) => {
  if (!orderNumber || orderNumber.length < 13) {
    return orderNumber;
  }
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  return `${datePart}-${dailyPart}-${destPart}`;
};

// [新規追加] YYYYMMDD形式の文字列をYYYY/MM/DD形式に変換するヘルパー関数
const formatShippingDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) {
    return dateStr; // フォーマットが不正な場合は元の文字列を返す
  }
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${year}/${month}/${day}`;
};

// [変更] 注文ステータスの表示名と色を定義
const statusMap = {
  new: { text: '新規', bg: 'primary' },
  processed: { text: '受付済み', bg: 'success' },
  shipped: { text: '出荷準備完了', bg: 'danger' }
};
const getTrackingUrl = (carrier, trackingNumber) => {
  if (!carrier || !trackingNumber) return null;
  const encodedTrackingNumber = encodeURIComponent(trackingNumber);
  switch (carrier) {
    case 'yupri':
      return `https://www.post.japanpost.jp/receive/tracking/result.php?code=${encodedTrackingNumber}`;
    case 'istar':
      return `https://corp.fukutsu.co.jp/situation/tracking_no_search?tracking_no=${encodedTrackingNumber}`;
    default:
      return null;
  }
};

function EmployeeOrderSearch() {
  const [orders, setOrders] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // 【変更点】ページネーション用のstateを追加
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    document.title = '担当顧客 注文履歴 | My Order System';
    const user = auth.currentUser;
    if (!user) {
      setError('ログインしていません。');
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersDataMap = new Map();
        usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
        setUsersMap(usersDataMap);

        const companiesSnapshot = await getDocs(collection(db, 'companies'));
        const companiesDataMap = new Map();
        companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
        setCompaniesMap(companiesDataMap);

        const followsCollectionRef = collection(db, 'follows');
        const qFollows = query(followsCollectionRef, where('employeeId', '==', user.uid));
        const followsSnapshot = await getDocs(qFollows);
        const followedCustomerIds = followsSnapshot.docs.map(doc => doc.data().customerId);

        if (followedCustomerIds.length === 0) {
          setLoading(false);
          return;
        }

        const ordersCollectionRef = collection(db, 'orders');
        const qOrders = query(ordersCollectionRef, where('orderedByUid', 'in', followedCustomerIds));
        const ordersSnapshot = await getDocs(qOrders);
        let fetchedOrders = ordersSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

        fetchedOrders.sort((a, b) => b.orderNumber.localeCompare(a.orderNumber));
        setOrders(fetchedOrders);

      } catch (err) {
        setError('履歴データの取得に失敗しました。');
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    const lowercasedFilter = searchTerm.toLowerCase();

    return orders.filter(order => {
      const customer = usersMap.get(order.orderedByUid);
      const company = companiesMap.get(customer?.companyId);
      const customerName = customer?.personName || '';
      const companyName = company?.companyName || '';
      const detailsToSearch = (order.invoiceDetails || order.propertyName || '').toLowerCase();

      const hasMatchingDestination = order.shippingDestinations.some(dest =>
        dest.orderNumber.toLowerCase().includes(lowercasedFilter)
      );

      return (
        customerName.toLowerCase().includes(lowercasedFilter) ||
        companyName.toLowerCase().includes(lowercasedFilter) ||
        detailsToSearch.includes(lowercasedFilter) ||
        hasMatchingDestination
      );
    });
  }, [orders, searchTerm, usersMap, companiesMap]);

  // 【変更点】ページネーション用の計算
  const indexOfLastOrder = currentPage * itemsPerPage;
  const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header
            as="h2"
            className="p-3"
          >
            担当顧客 注文履歴検索
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="発注番号、顧客の氏名・会社名、送り状の記載事項で検索..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // 検索時に1ページ目に戻す
                }}
              />
            </Form.Group>

            {
              loading ?
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
                :
                (
                  <>
                    <p>
                      {filteredOrders.length}件の注文が見つかりました。
                    </p>
                    <Accordion>
                      {currentOrders.flatMap(order =>
                        order.shippingDestinations.map((dest) => {
                          // [変更] 後方互換性のためのロジックを追加
                          const invoiceDetailsText = order.invoiceDetails ||
                            `${order.propertyName || ''}${order.propertyName && order.remarks ? ' ' : ''}${order.remarks || ''}` ||
                            '(記載なし)';
                          const statusInfo = statusMap[order.status] || { text: order.status || '新規', bg: 'secondary' };
                          const trackingUrl = getTrackingUrl(order.shippingCarrier, dest.trackingNumber);
                          return (
                            <Accordion.Item
                              eventKey={dest.orderNumber}
                              key={dest.orderNumber}
                            >
                              <Accordion.Header>
                                <Container fluid>
                                  <Row className="w-100 align-items-center g-2">
                                    <Col xs={12} md={5} className="text-center text-md-start">
                                      <div>
                                        発注No.
                                      </div>
                                      <span className="fw-bold fs-5">
                                        {formatDisplayOrderNumber(dest.orderNumber)}
                                      </span>
                                      <hr />
                                      <div>
                                        送り状No.
                                        <span>
                                          {
                                            order.shippingCarrier === 'yupri' &&
                                            <span className="badge" style={{ backgroundColor: '#CCCC66', color: 'black' }}>
                                              日本郵便
                                            </span>
                                          }
                                          {
                                            order.shippingCarrier === 'istar' &&
                                            <span className="badge" style={{ backgroundColor: '#FFCC33', color: 'black' }}>
                                              福山通運
                                            </span>
                                          }
                                        </span>
                                      </div>
                                      <span className="fw-bold fs-5">
                                        {
                                          dest.trackingNumber ?
                                            <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                              {dest.trackingNumber}
                                            </a>
                                            :
                                            'ー'
                                        }
                                      </span>
                                    </Col>
                                    <Col xs={12} md={6} className="text-center text-md-start">
                                      <div className="text-muted">
                                        <small className="text-muted">
                                          発注者:
                                        </small>
                                        <span>
                                          {usersMap.get(order.orderedByUid)?.personName || '不明'} 様
                                        </span>
                                      </div>
                                      <div>
                                        <small className="text-muted">
                                          送付先:
                                        </small>
                                        <span>
                                          {dest.recipientName} 様
                                        </span>
                                      </div>
                                      <div>
                                        <small className="text-muted">
                                          発注日時:
                                        </small>
                                        <span>
                                          {order.orderedAt?.toDate().toLocaleString('ja-JP')}
                                        </span>
                                      </div>
                                      <div>
                                        <small className="text-muted">
                                          出荷予定日:
                                        </small>
                                        <strong>
                                          {formatShippingDate(order.estimatedShippingDate)}
                                        </strong>
                                      </div>
                                    </Col>
                                    <Col xs={12} md={1} className="text-center text-md-end">
                                      {/* [変更] statusInfoを使ってバッジを表示 */}
                                      <Badge bg={statusInfo.bg}>
                                        {statusInfo.text}
                                      </Badge>
                                    </Col>
                                    <Col xs={12} className='border p-2 bg-white'>
                                      <div className='text-muted small'>
                                        送り状に記載する事項:
                                      </div>
                                      <div>
                                        {invoiceDetailsText}
                                      </div>
                                    </Col>
                                  </Row>
                                </Container>
                              </Accordion.Header>
                              <Accordion.Body>
                                <h5>
                                  注文詳細
                                </h5>
                                <Table bordered size="sm">
                                  <tbody>
                                    {/* [変更] 表示項目を'送り状に記載する事項'に統合 */}
                                    <tr>
                                      <td className="bg-light" style={{ width: '150px' }}>
                                        送り状に記載する事項
                                      </td>
                                      <td style={{ whiteSpace: 'pre-wrap' }}>
                                        {invoiceDetailsText}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="bg-light">
                                        送付先会社名
                                      </td>
                                      <td>
                                        {dest.shippingCompanyName || '(記載なし)'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="bg-light">
                                        支店・部署名
                                      </td>
                                      <td>
                                        {`${dest.branchName || ''} ${dest.departmentName || ''}`.trim() || '(記載なし)'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="bg-light">
                                        送付先住所
                                      </td>
                                      <td>
                                        〒{dest.postalCode} {dest.address1} {dest.address2} {dest.address3}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="bg-light">
                                        電話番号
                                      </td>
                                      <td>
                                        {dest.phoneNumber || '(記載なし)'}
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                {dest.trackingNumber && (
                                  <>
                                    <h5 className="mt-3">配送情報</h5>
                                    <Table bordered size="sm">
                                      <tbody>
                                        <tr>
                                          <td className="bg-light" style={{ width: '150px' }}>
                                            配送業者
                                          </td>
                                          <td>
                                            {
                                              order.shippingCarrier === 'yupri' ?
                                                '日本郵便'
                                                :
                                                order.shippingCarrier === 'istar' ?
                                                  '福山通運'
                                                  :
                                                  '不明'
                                            }
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className="bg-light">
                                            お問い合わせ番号
                                          </td>
                                          <td>
                                            {/* 【変更点】trackingUrlが存在する場合のみリンクにする */}
                                            {
                                              trackingUrl ?
                                                (
                                                  <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                                    {dest.trackingNumber}
                                                  </a>
                                                )
                                                :
                                                (
                                                  <span>
                                                    {dest.trackingNumber}
                                                  </span>
                                                )}
                                            <Button
                                              variant="link"
                                              size="sm"
                                              onClick={() => navigator.clipboard.writeText(dest.trackingNumber)}
                                            >
                                              <Clipboard />
                                            </Button>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  </>
                                )}
                                <h5 className="mt-3">
                                  商品リスト
                                </h5>
                                <Table striped bordered size="sm">
                                  <thead>
                                    <tr>
                                      <th>
                                        品番
                                      </th>
                                      <th>
                                        数量
                                      </th>
                                      <th>
                                        備考
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.products.map((product, pIndex) => (
                                      <tr key={pIndex}>
                                        <td>
                                          {product.partNumber}
                                        </td>
                                        <td>
                                          {product.quantity}
                                        </td>
                                        <td>
                                          {product.remarks}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                                <h5 className="mt-3">
                                  発注者内容
                                </h5>
                                <Table bordered size="sm">
                                  <tbody>
                                    {(() => {
                                      // ★ 修正: 注文情報から発注者と会社の情報を取得
                                      const customer = usersMap.get(order.orderedByUid);
                                      const company = customer ? companiesMap.get(customer.companyId) : null;

                                      // ★ 修正: 会社名を整形（法人格を結合）
                                      const fullCompanyName = company
                                        ? `${company.companyPrefix || ''}${company.companyName}${company.companySuffix || ''}`
                                        : '(会社情報なし)';

                                      // ★ 修正: 支店・部署名を整形
                                      const branchAndDept = company
                                        ? `${company.branchName || ''} ${company.departmentName || ''}`.trim()
                                        : '';

                                      // ★ 修正: 住所を整形
                                      const fullAddress = customer
                                        ? `〒${customer.zipCode || ''} ${customer.address1 || ''}${customer.address2 || ''}${customer.address3 || ''}`
                                        : '(住所情報なし)';

                                      return (
                                        <>
                                          <tr>
                                            <td className="bg-light" style={{ width: '150px' }}>
                                              発注会社名
                                            </td>
                                            {/* ★ 修正: 整形した会社名を表示 */}
                                            <td>
                                              {fullCompanyName}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="bg-light">
                                              支店・部署名
                                            </td>
                                            {/* ★ 修正: 整形した支店・部署名を表示 */}
                                            <td>
                                              {branchAndDept || '(記載なし)'}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="bg-light">
                                              発注者名
                                            </td>
                                            <td>
                                              {usersMap.get(order.orderedByUid)?.personName || '不明'}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="bg-light">
                                              発注者住所
                                            </td>
                                            {/* ★ 修正: 整形した住所を表示 */}
                                            <td>
                                              {fullAddress}
                                            </td>
                                          </tr>
                                          <tr>
                                            <td className="bg-light">
                                              発注者電話番号
                                            </td>
                                            {/* ★ 修正: 発注者の電話番号を表示 */}
                                            <td>
                                              {customer?.phone || '(電話番号なし)'}
                                            </td>
                                          </tr>
                                        </>
                                      );
                                    })()}
                                  </tbody>
                                </Table>
                              </Accordion.Body>
                            </Accordion.Item>
                          );
                        })
                      )}
                    </Accordion>

                    {/* 【変更点】ページネーションコントロールを追加 */}
                    {
                      totalPages > 1 &&
                      (
                        <Pagination className="justify-content-center mt-3">
                          <Pagination.First
                            onClick={() => paginate(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {[...Array(totalPages).keys()].map(number => (
                            <Pagination.Item
                              key={number + 1}
                              active={number + 1 === currentPage}
                              onClick={() => paginate(number + 1)}
                            >
                              {number + 1}
                            </Pagination.Item>
                          ))}
                          <Pagination.Next
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => paginate(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      )
                    }
                  </>
                )
            }
            {
              !loading &&
              filteredOrders.length === 0 &&
              <p className="text-muted">
                担当している顧客の注文履歴はありません。
              </p>
            }
            <hr className="my-4" />
            <Link to="/mainmenu">
              メインメニューに戻る
            </Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default EmployeeOrderSearch;
